import type { AssistantObjectRef } from '@contracts/assistant';
import './glow.css';

/**
 * What Hana just touched, shown on the page (spec §8.1, D88): every element marked `data-object-ref="type:id"` (the
 * Workplace cards, rows and nodes) glows for `GLOW_MS`, and a hovered chip outlines its card. A box-shadow, never an
 * outline, a border or a left bar, in violet, so it never reads as the board's primary-coloured selection.
 * The marks are data attributes, never classes: React owns a card's className and re-renders it (the live flash,
 * D204), which would wipe a class set here (P4 criterion 2).
 */

export const GLOW_MS = 2000;
export const GLOW_ATTR = 'data-hana-glow';
export const HOVER_ATTR = 'data-hana-hover';

const selectorOf = (r: AssistantObjectRef) => `[data-object-ref="${r.type}:${CSS.escape(r.id)}"]`;

/** How long a glow keeps looking for a card React has not committed yet (a refetched row, P4 G8 review F1). */
export const GLOW_WAIT_MS = 500;

// One removal timer per element: a second glow cancels the first one's, so it lasts its own full GLOW_MS (F6).
const timers = new WeakMap<HTMLElement, number>();

function mark(el: HTMLElement, value: 'move' | 'still'): void {
  const old = timers.get(el);
  if (old !== undefined) window.clearTimeout(old);
  el.removeAttribute(GLOW_ATTR);
  void el.offsetWidth;                                           // restarts the animation on a second glow
  el.setAttribute(GLOW_ATTR, value);
  timers.set(el, window.setTimeout(() => { timers.delete(el); el.removeAttribute(GLOW_ATTR); }, GLOW_MS));
}

export function glowObjects(refs: readonly AssistantObjectRef[], { reduced }: { reduced: boolean }): void {
  if (refs.length === 0) return;
  const value = reduced ? 'still' : 'move';
  let waiting = [...refs];
  const started = performance.now();
  const look = () => {
    waiting = waiting.filter((r) => {
      const els = document.querySelectorAll<HTMLElement>(selectorOf(r));
      for (const el of els) mark(el, value);
      return els.length === 0;
    });
    if (waiting.length > 0 && performance.now() - started < GLOW_WAIT_MS) requestAnimationFrame(look);
  };
  requestAnimationFrame(look);
}

export function hoverObject(r: AssistantObjectRef, on: boolean): void {
  for (const el of document.querySelectorAll<HTMLElement>(selectorOf(r))) el.toggleAttribute(HOVER_ATTR, on);
}
